import { jsPDF } from "jspdf";
import { formatCLP } from "./currencyUtils";
import { STATUS_LABELS } from "./quoteModel";

const formatDate = (iso) => {
	if (!iso) return "—";
	try {
		return new Date(iso).toLocaleDateString("es-CL");
	} catch (e) {
		return "—";
	}
};

const getValidityLabel = (quote) => {
	if (quote.validityDays === "custom" || quote.customValidityDays) {
		return `${quote.customValidityDays || quote.validityDays} días`;
	}
	return `${quote.validityDays || 15} días`;
};

const getPaymentLabel = (quote) => {
	if (quote.paymentMethod === "Otra") {
		return quote.customPaymentMethod || "Otra";
	}
	return quote.paymentMethod || "—";
};

/**
 * Genera un PDF estructurado desde los datos de la cotización.
 * Usa jspdf (ya presente en el proyecto). No almacena el archivo.
 */
export const buildQuotePdfDocument = (quote) => {
	const doc = new jsPDF();
	const issuer = quote.issuerSnapshot || {};
	const client = quote.client || {};
	const seller = quote.sellerSnapshot || {};
	const isExenta = issuer.tipoTributario === "exenta";
	let y = 16;

	const ensureSpace = (needed = 12) => {
		if (y + needed > 280) {
			doc.addPage();
			y = 16;
			doc.setFontSize(8);
			doc.setTextColor(120);
			doc.text("Cotización generada por DeNegocios.cl", 105, 290, { align: "center" });
			doc.setTextColor(0);
		}
	};

	const sectionTitle = (title) => {
		ensureSpace(10);
		doc.setFont("helvetica", "bold");
		doc.setFontSize(11);
		doc.text(title, 14, y);
		y += 2;
		doc.setDrawColor(180);
		doc.line(14, y, 196, y);
		y += 6;
		doc.setFont("helvetica", "normal");
		doc.setFontSize(9);
	};

	const line = (label, value) => {
		ensureSpace(6);
		doc.setFont("helvetica", "bold");
		doc.text(`${label}:`, 14, y);
		doc.setFont("helvetica", "normal");
		const text = doc.splitTextToSize(String(value || "—"), 140);
		doc.text(text, 50, y);
		y += Math.max(5, text.length * 4.5);
	};

	// Encabezado empresa
	doc.setFillColor(issuer.logoColor || "#2c3840");
	doc.roundedRect(14, 10, 18, 14, 2, 2, "F");
	doc.setTextColor(255);
	doc.setFont("helvetica", "bold");
	doc.setFontSize(10);
	doc.text(issuer.logo || "DN", 23, 19, { align: "center" });
	doc.setTextColor(0);

	doc.setFont("helvetica", "bold");
	doc.setFontSize(13);
	doc.text(issuer.nombreLegal || "Empresa emisora", 36, 16);
	doc.setFont("helvetica", "normal");
	doc.setFontSize(9);
	if (issuer.nombreFantasia) {
		doc.text(issuer.nombreFantasia, 36, 21);
	}
	doc.text(`RUT: ${issuer.rut || "—"}`, 36, 26);
	doc.text(`${issuer.telefono || ""}  ·  ${issuer.correo || ""}`, 36, 31);
	doc.text(issuer.direccion || "", 36, 36);

	doc.setFont("helvetica", "bold");
	doc.setFontSize(16);
	doc.text("COTIZACIÓN", 196, 16, { align: "right" });
	doc.setFont("helvetica", "normal");
	doc.setFontSize(10);
	doc.text(quote.number || "BORRADOR", 196, 22, { align: "right" });
	doc.setFontSize(8);
	doc.text(`Estado: ${STATUS_LABELS[quote.status] || quote.status}`, 196, 27, {
		align: "right",
	});
	doc.text(`Fecha: ${formatDate(quote.issuedAt || quote.createdAt)}`, 196, 32, {
		align: "right",
	});
	doc.text(`Vigencia: ${getValidityLabel(quote)}`, 196, 37, { align: "right" });

	y = 48;

	if (isExenta) {
		doc.setFillColor(232, 245, 233);
		doc.roundedRect(14, y - 4, 182, 10, 2, 2, "F");
		doc.setFont("helvetica", "bold");
		doc.setFontSize(9);
		doc.setTextColor(39, 103, 73);
		doc.text("Documento emitido por una empresa exenta de IVA.", 105, y + 2, {
			align: "center",
		});
		doc.setTextColor(0);
		y += 14;
	}

	sectionTitle("Datos del cliente");
	line("Nombre", client.nombre);
	line("RUT", client.rut);
	line("Correo", client.correo);
	line("Teléfono", client.telefono);
	line("Giro", client.giro);
	line("Domicilio", client.domicilio);
	line("Comuna", client.comuna);

	sectionTitle("Datos del vendedor");
	line("Nombre", seller.nombre);
	line("Correo", seller.correo);
	line("Teléfono", seller.telefono);

	sectionTitle("Productos y servicios");
	ensureSpace(12);
	doc.setFont("helvetica", "bold");
	doc.text("Descripción", 14, y);
	doc.text("Cant.", 110, y);
	doc.text("P. unit.", 128, y);
	doc.text("Desc.", 152, y);
	doc.text("Total", 196, y, { align: "right" });
	y += 2;
	doc.setDrawColor(200);
	doc.line(14, y, 196, y);
	y += 5;
	doc.setFont("helvetica", "normal");

	(quote.items || []).slice(0, 5).forEach((item) => {
		ensureSpace(14);
		const title = doc.splitTextToSize(item.nombre || "—", 90);
		doc.setFont("helvetica", "bold");
		doc.text(title, 14, y);
		doc.setFont("helvetica", "normal");
		doc.text(String(item.cantidad), 110, y);
		doc.text(formatCLP(item.precioUnitario), 128, y);
		doc.text(formatCLP(item.montoDescuento), 152, y);
		doc.text(formatCLP(item.total), 196, y, { align: "right" });
		y += title.length * 4.2;
		if (item.descripcion) {
			const desc = doc.splitTextToSize(item.descripcion, 90);
			doc.setFontSize(8);
			doc.setTextColor(90);
			doc.text(desc, 14, y);
			doc.setTextColor(0);
			doc.setFontSize(9);
			y += desc.length * 3.8 + 2;
		} else {
			y += 4;
		}
	});

	y += 4;
	sectionTitle("Totales");
	const addTotal = (label, value, bold = false) => {
		ensureSpace(6);
		doc.setFont("helvetica", bold ? "bold" : "normal");
		doc.text(label, 130, y);
		doc.text(formatCLP(value), 196, y, { align: "right" });
		y += 5;
	};

	if (isExenta) {
		addTotal("Subtotal", quote.subtotal);
		addTotal("Descuentos", quote.lineDiscountsTotal + (quote.globalDiscount?.amount || 0));
		addTotal("Total exento", quote.grandTotal, true);
	} else {
		addTotal("Subtotal", quote.subtotal);
		addTotal("Descuentos", quote.lineDiscountsTotal + (quote.globalDiscount?.amount || 0));
		addTotal("Neto", quote.netTotal);
		addTotal(`IVA ${quote.taxRate || 19}%`, quote.taxTotal);
		addTotal("Total", quote.grandTotal, true);
	}

	sectionTitle("Condiciones");
	line("Forma de pago", getPaymentLabel(quote));
	line("Plazo de ejecución", quote.executionTime);
	line("Condiciones comerciales", quote.conditions);
	line("Observaciones", quote.notes || "Sin observaciones");

	// Pie
	const pageCount = doc.internal.getNumberOfPages();
	for (let i = 1; i <= pageCount; i += 1) {
		doc.setPage(i);
		doc.setFontSize(8);
		doc.setTextColor(120);
		doc.text("Cotización generada por DeNegocios.cl", 105, 290, { align: "center" });
		doc.text(`Página ${i} de ${pageCount}`, 196, 290, { align: "right" });
	}

	return doc;
};

export const downloadQuotePdf = (quote) => {
	const doc = buildQuotePdfDocument(quote);
	const fileName = `${quote.number || quote.id || "cotizacion"}.pdf`;
	doc.save(fileName);
};

export const openQuotePdf = (quote) => {
	const doc = buildQuotePdfDocument(quote);
	window.open(doc.output("bloburl"), "_blank");
};
